/**
 * Debug Logger - Utilitaire pour capturer les logs de débogage
 * Utilise le serveur de logging si disponible, sinon localStorage comme fallback
 */

import { isProd } from './envHelper';

const LOG_STORAGE_KEY = 'snapbooth_debug_logs';
const MAX_LOGS = 1000;
const SERVER_ENDPOINT = 'http://127.0.0.1:7242/ingest/c8602e69-d639-4293-8996-b4e333e7e3b7';

// Cache pour éviter les tentatives répétées si le serveur n'est pas disponible
let serverAvailable: boolean | null = null;
let lastServerCheck = 0;
const SERVER_CHECK_INTERVAL = 30000; // Vérifier toutes les 30 secondes

export interface DebugLogEntry {
  id: string;
  timestamp: number;
  location: string;
  message: string;
  data?: Record<string, unknown>;
  sessionId: string;
  runId: string;
  hypothesisId: string;
}

/**
 * Vérifie si le serveur de logging est disponible
 */
function isServerAvailable(): boolean {
  // En production, ne pas tenter de connexion
  if (isProd()) {
    return false;
  }

  // Si on a déjà vérifié récemment, utiliser le cache
  const now = Date.now();
  if (serverAvailable !== null && now - lastServerCheck < SERVER_CHECK_INTERVAL) {
    return serverAvailable;
  }

  // Par défaut, on suppose que le serveur n'est pas disponible
  // La première tentative de fetch déterminera si c'est le cas
  return serverAvailable ?? false;
}

/**
 * Envoie un log au serveur et le stocke dans localStorage comme fallback
 */
export function debugLog(entry: Omit<DebugLogEntry, 'id' | 'timestamp'>): void {
  // P2.cleanup : no-op en production pour éliminer l'overhead.
  // Tous les appelants (useAppCoordinator, CanvasArea, etc.) restent valides.
  if (isProd()) return;

  const logEntry: DebugLogEntry = {
    id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: Date.now(),
    ...entry,
  };

  // Essayer d'envoyer au serveur seulement si disponible (non bloquant)
  if (isServerAvailable() || serverAvailable === null) {
    // Créer un AbortController pour le timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    fetch(SERVER_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logEntry),
      signal: controller.signal,
    })
      .then(() => {
        clearTimeout(timeoutId);
        // Si succès, marquer le serveur comme disponible
        if (serverAvailable !== true) {
          serverAvailable = true;
          lastServerCheck = Date.now();
        }
      })
      .catch(() => {
        clearTimeout(timeoutId);
        // Si erreur CSP ou réseau, marquer le serveur comme indisponible
        if (serverAvailable !== false) {
          serverAvailable = false;
          lastServerCheck = Date.now();
        }
        // Ignorer les erreurs silencieusement (CSP, réseau, etc.)
      });
  }

  // Stocker dans localStorage comme fallback
  try {
    const existingLogs = getStoredLogs();
    existingLogs.push(logEntry);

    // Limiter le nombre de logs
    if (existingLogs.length > MAX_LOGS) {
      existingLogs.splice(0, existingLogs.length - MAX_LOGS);
    }

    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(existingLogs));
  } catch (error) {
    // Si localStorage est plein ou indisponible, log dans la console
    console.warn('[DebugLogger] Failed to store log:', error);
    console.log('[DebugLog]', logEntry);
  }
}

/**
 * Récupère tous les logs stockés
 */
export function getStoredLogs(): DebugLogEntry[] {
  try {
    const stored = localStorage.getItem(LOG_STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as DebugLogEntry[];
  } catch {
    return [];
  }
}

/**
 * Efface tous les logs stockés
 */
export function clearStoredLogs(): void {
  try {
    localStorage.removeItem(LOG_STORAGE_KEY);
  } catch {
    // Ignorer les erreurs
  }
}

/**
 * Exporte les logs au format JSON
 */
export function exportLogs(): string {
  return JSON.stringify(getStoredLogs(), null, 2);
}

/**
 * Expose les fonctions globalement pour utilisation dans la console
 */
if (typeof window !== 'undefined') {
  (
    window as unknown as {
      getDebugLogs: typeof getStoredLogs;
      exportDebugLogs: typeof exportLogs;
      clearDebugLogs: typeof clearStoredLogs;
    }
  ).getDebugLogs = getStoredLogs;
  (
    window as unknown as {
      getDebugLogs: typeof getStoredLogs;
      exportDebugLogs: typeof exportLogs;
      clearDebugLogs: typeof clearStoredLogs;
    }
  ).exportDebugLogs = exportLogs;
  (
    window as unknown as {
      getDebugLogs: typeof getStoredLogs;
      exportDebugLogs: typeof exportLogs;
      clearDebugLogs: typeof clearStoredLogs;
    }
  ).clearDebugLogs = clearStoredLogs;
}
